import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Modal } from './Modal';
import { Button } from './Button';

const ScannerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${props => props.theme.spacing.lg};
  min-height: 300px;
  justify-content: center;
`;

const Instructions = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  
  h3 {
    font-size: ${props => props.theme.typography.fontSize.lg};
    margin-bottom: ${props => props.theme.spacing.md};
    color: ${props => props.theme.colors.text.primary};
  }
  
  p {
    color: ${props => props.theme.colors.text.secondary};
    margin-bottom: ${props => props.theme.spacing.sm};
  }
`;

const BarcodeDisplay = styled.div`
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.lg};
  background: rgba(135, 206, 235, 0.1);
  border-radius: ${props => props.theme.borderRadius.md};
  margin: ${props => props.theme.spacing.lg} 0;
  min-width: 300px;
  text-align: center;
  border: 2px solid ${props => props.theme.colors.primary};
`;

const HiddenInput = styled.input`
  position: absolute;
  left: -9999px;
  opacity: 0;
`;

const StatusMessage = styled.p`
  color: ${props => props.success ? props.theme.colors.success : props.theme.colors.error};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  margin-top: ${props => props.theme.spacing.md};
`;

export const PhysicalBarcodeScanner = ({ isOpen, onClose, onScan }) => {
  const [barcode, setBarcode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [lastScanned, setLastScanned] = useState('');
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setScanning(true);
      setBarcode('');
      setLastScanned('');
    }
  }, [isOpen]);

  const handleKeyDown = (e) => {
    // Los escáneres de código de barras normalmente terminan con Enter
    if (e.key === 'Enter' && barcode.length > 0) {
      e.preventDefault();
      handleScan();
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setBarcode(value);
    
    // Resetear el timeout anterior
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Si el valor tiene más de 6 caracteres, esperar 500ms y escanear automáticamente
    if (value.length > 6) {
      timeoutRef.current = setTimeout(() => {
        handleScan();
      }, 500);
    }
  };

  const handleScan = () => {
    if (barcode && barcode.length > 0) {
      setLastScanned(barcode);
      onScan(barcode);
      setBarcode('');
      
      // Mantener el foco en el input para escaneos continuos
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const handleClose = () => {
    setScanning(false);
    setBarcode('');
    setLastScanned('');
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Escáner de Código de Barras"
      size="medium"
    >
      <ScannerContainer>
        <Instructions>
          <h3>Escáner Físico Detectado</h3>
          <p>Use su escáner de código de barras para escanear el producto</p>
          <p>El código se capturará automáticamente</p>
        </Instructions>

        {scanning && (
          <>
            <HiddenInput
              ref={inputRef}
              type="text"
              value={barcode}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={() => inputRef.current?.focus()}
              autoFocus
            />
            
            {barcode && (
              <BarcodeDisplay>
                {barcode}
              </BarcodeDisplay>
            )}
            
            {lastScanned && (
              <StatusMessage success>
                ✓ Último escaneado: {lastScanned}
              </StatusMessage>
            )}
            
            <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <Button variant="outlined" onClick={handleClose}>
                Cerrar Escáner
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => {
                  handleClose();
                  // Notificar al componente padre que quiere usar la cámara
                  if (window.useCameraInstead) {
                    window.useCameraInstead();
                  }
                }}
              >
                Usar Cámara
              </Button>
            </div>
          </>
        )}
      </ScannerContainer>
    </Modal>
  );
};